// @ts-ignore
import "./Planograms.scss";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { TabPanel, TabView } from "primereact/tabview";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

// @ts-ignore
import { ReactComponent as AddCircleIcon } from "../../assets/images/add-circle-icon.svg";
// @ts-ignore
import { ReactComponent as AddCircleDarkIcon } from "../../assets/images/add-circle-dark.svg";
// @ts-ignore
import { ReactComponent as TrashIcon } from "../../assets/images/trash-icon.svg";
// @ts-ignore
import { ReactComponent as RemoveLineRedBgIcon } from "../../assets/images/stock-levels/button-remove-line-red-bg.svg";
// @ts-ignore
import { ReactComponent as KeyboardArrowDownIcon } from "../../assets/images/planograms/keyboard-arrow-down.svg";
// @ts-ignore
import { ReactComponent as KeyboardArrowUpIcon } from "../../assets/images/planograms/keyboard-arrow-up.svg";

import DataTable, {
  FILTER_TYPES,
  DataTableContext,
  DataTableProvider,
} from "../../component/datatable2";
import DataTableSubtable, {
  type DataTableSubtableHandle,
  type SubtableFetchResult,
  type SubtableSearchCriteria,
} from "../../component/datatable-subtable/DataTableSubtable";

import ActionButton from "../../component/actionbutton/ActionButton";
import ActionMenu from "../../component/actionmenu/ActionMenu";
import ConfirmDialog from "../../component/confirmdialog/ConfirmDialog";
import { MessageContext } from "../../context/MessageContext";
import { useAuth } from "../../hook/useAuth";
import { DeviceService } from "../../service/DeviceService";
import { PlanogramService } from "../../service/PlanogramService";
import { PlanogramPendingService } from "../../service/PlanogramPendingService";
import { PlanogramTransformUtils } from "./utils/PlanogramTransformUtils";
import { isPlanogramComplete as isPlanogramCompleteUtil } from "./utils/PlanogramUtils";
import { Constants } from "../../utils/Constants";
import useNavigationContext from "../../utils/navigationcontext/NavigationContext";
import { RenderedHelper } from "../../utils/render/RenderedHelper";
import CreatePlanogramDialog from "./dialogs/CreatePlanogramDialog";
import PlanogramDeactivateDialog from "./dialogs/PlanogramDeactivateDialog";
import CopyPlanogramToDeviceDialog from "./dialogs/CopyPlanogramToDeviceDialog";

// @ts-ignore
import { ReactComponent as ArrowDetailsIcon } from "../../assets/images/arrow-details.svg";

const ExpandableDataTable = DataTable as any;

type SearchCriteria = SubtableSearchCriteria & {
  baseQuery: string;
  filters: Record<string, unknown>;
  showOnlyActive?: boolean;
};

type SelectOption = {
  id: string;
  label: string;
  uuid?: string;
  externalId?: string;
};

type PlanogramRow = {
  id?: string;
  uuid?: string;
  externalId?: string;
  systemId?: string;
  externalSystemId?: string;
  planogramId?: string;
  name?: string;
  createdAt?: string;
  tiedDevicesCount?: number;
  rowsCount?: number;
  columnsCount?: number;
  rows?: number;
  columns?: number;
  cells?: unknown;
  fingerprintCells?: string;
  lockResult?: unknown;
  status?: string;
};

type DeviceRow = {
  id?: string;
  uuid?: string;
  systemId?: string;
  externalSystemId?: string;
  name?: string;
  location?: string;
  tid?: string;
  assignedDate?: string;
  planogramAssignments?: {
    assignedDate?: string;
  };
  rows?: number;
  cols?: number;
  healthState?: string;
};

type DeviceListApiRow = Omit<DeviceRow, "location" | "tid"> & {
  location?:
    | string
    | {
        id?: string;
        name?: string;
      }
    | null;
  terminal?: {
    terminalId?: string | null;
  } | null;
  tid?: string | null;
  planogram?: {
    id?: string;
    uuid?: string;
    name?: string;
  } | null;
};

type MessageContextValue = {
  showErrorMessage: (message: string) => void;
  showSuccessMessage: (message: string) => void;
};

type DataTableContextValue = {
  searchCriteria: SearchCriteria;
  setSearchCriteria: React.Dispatch<React.SetStateAction<SearchCriteria>>;
};

const createDefaultDevicesSearchCriteria = (): SearchCriteria => ({
  first: 0,
  rows: 25,
  sortField: "name",
  sortOrder: 1,
  baseQuery: "",
  filters: {},
  showOnlyActive: false,
});

const getPlanogramKey = (planogram?: PlanogramRow | null): string =>
  planogram?.id || planogram?.uuid || "";

const getPlanogramRows = (planogram?: PlanogramRow | null): number | null =>
  planogram?.rowsCount ?? planogram?.rows ?? null;

const getPlanogramColumns = (planogram?: PlanogramRow | null): number | null =>
  planogram?.columnsCount ?? planogram?.columns ?? null;

const getAssignedDate = (row: DeviceRow): string | undefined =>
  row.assignedDate || row.planogramAssignments?.assignedDate;

const getDeviceKey = (device?: DeviceRow | null): string =>
  device?.id || device?.uuid || "";

const getDeviceListPlanogramKey = (device?: DeviceListApiRow | null): string =>
  device?.planogram?.id || device?.planogram?.uuid || "";

const normalizeDeviceListRow = (row: DeviceListApiRow): DeviceRow => ({
  ...row,
  location:
    typeof row.location === "string" ? row.location : row.location?.name || "",
  tid: row.tid || row.terminal?.terminalId || "",
  assignedDate: row.assignedDate || row.planogramAssignments?.assignedDate,
});

const getStringValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;

  const stringValue = String(value).trim();

  return stringValue || null;
};

const isGridCells = (cells: unknown): boolean =>
  Array.isArray(cells) && cells.every((row) => Array.isArray(row));

const normalizePlanogramForCopyDialog = (
  rowPlanogram: PlanogramRow,
  details: unknown,
  fallbackPlanogramKey: string,
): PlanogramRow => {
  const detailsPlanogram =
    details && typeof details === "object"
      ? (details as PlanogramRow)
      : ({} as PlanogramRow);

  const detailsRows = getPlanogramRows(detailsPlanogram);
  const detailsColumns = getPlanogramColumns(detailsPlanogram);
  const fallbackRows = getPlanogramRows(rowPlanogram);
  const fallbackColumns = getPlanogramColumns(rowPlanogram);

  const resolvedRows = detailsRows ?? fallbackRows;
  const resolvedColumns = detailsColumns ?? fallbackColumns;

  let normalizedCells = detailsPlanogram.cells ?? rowPlanogram.cells;

  if (resolvedRows && resolvedColumns) {
    try {
      // Match PlanogramPreviewPage / PlanogramDetails: always run the
      // details response through the API -> grid transformer before
      // giving it to CopyPlanogramToDeviceDialog.
      normalizedCells = PlanogramTransformUtils.apiToGrid({
        ...detailsPlanogram,
        rows: resolvedRows,
        columns: resolvedColumns,
      });
    } catch (error) {
      console.error(
        "Planograms::normalizePlanogramForCopyDialog apiToGrid error:",
        error,
      );

      if (normalizedCells && !isGridCells(normalizedCells)) {
        normalizedCells = undefined;
      }
    }
  }

  const normalizedId =
    getStringValue(detailsPlanogram.id) ??
    getStringValue(rowPlanogram.id) ??
    fallbackPlanogramKey;

  const normalizedUuid =
    getStringValue(detailsPlanogram.uuid) ?? getStringValue(rowPlanogram.uuid);

  const normalizedExternalId =
    getStringValue(detailsPlanogram.externalId) ??
    getStringValue(rowPlanogram.externalId) ??
    normalizedUuid ??
    normalizedId;

  return {
    ...rowPlanogram,
    ...detailsPlanogram,
    id: normalizedId,
    uuid: normalizedUuid ?? undefined,
    externalId: normalizedExternalId,
    rows: resolvedRows ?? undefined,
    rowsCount: resolvedRows ?? rowPlanogram.rowsCount,
    columns: resolvedColumns ?? undefined,
    columnsCount: resolvedColumns ?? rowPlanogram.columnsCount,
    cells: normalizedCells,
  };
};

type InlineDevicesTableProps = {
  planogram: PlanogramRow;
  refreshToken?: number;
  canWrite: boolean;
  navigationContext: any;
  onAddDevice: (planogram: PlanogramRow) => void;
  onAfterRemoveDevice: (planogram: PlanogramRow) => void;
  showErrorMessage: (message: string) => void;
  showSuccessMessage: (message: string) => void;
};

const PLANOGRAM_TAB_ROUTE_BY_INDEX: Record<number, string> = {
  0: "/planograms",
  1: "/planograms/pendings",
};

const PLANOGRAM_TAB_INDEX_BY_ROUTE: Record<string, number> = {
  "/planograms": 0,
  "/planograms/pendings": 1,
};

interface TabHeaderTemplateOptions {
  className?: string;
  selected?: boolean;
  ariaControls?: string;
}

const isModifiedLinkClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
): boolean => {
  return (
    event.button !== 0 ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    event.altKey
  );
};

const InlineDevicesTable = ({
  planogram,
  refreshToken,
  canWrite,
  navigationContext,
  onAddDevice,
  onAfterRemoveDevice,
  showErrorMessage,
  showSuccessMessage,
}: InlineDevicesTableProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tableRef = useRef<DataTableSubtableHandle>(null);

  const planogramKey = getPlanogramKey(planogram);

  const defaultDevicesSearchCriteria = useMemo<SearchCriteria>(
    () => createDefaultDevicesSearchCriteria(),
    [],
  );

  const [devicesSearchCriteria, setDevicesSearchCriteria] =
    useState<SearchCriteria>(defaultDevicesSearchCriteria);
  const [removingDeviceId, setRemovingDeviceId] = useState<string | null>(null);
  const [removedDeviceIds, setRemovedDeviceIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [deleteDeviceDialogState, setDeleteDeviceDialogState] = useState<{
    visible: boolean;
    device: DeviceRow | null;
  }>({
    visible: false,
    device: null,
  });

  useEffect(() => {
    setDevicesSearchCriteria(defaultDevicesSearchCriteria);
    setRemovedDeviceIds(new Set());
  }, [planogramKey, defaultDevicesSearchCriteria]);

  const normalizeDevicesCriteria = useCallback(
    (criteria?: Partial<SearchCriteria> | null): SearchCriteria => ({
      ...defaultDevicesSearchCriteria,
      ...criteria,
      first: criteria?.first ?? defaultDevicesSearchCriteria.first,
      rows: criteria?.rows ?? defaultDevicesSearchCriteria.rows,
      sortField:
        !criteria?.sortField || criteria.sortField === "null"
          ? defaultDevicesSearchCriteria.sortField
          : criteria.sortField,
      sortOrder:
        criteria?.sortOrder === null ||
        criteria?.sortOrder === undefined ||
        criteria?.sortOrder === 0
          ? defaultDevicesSearchCriteria.sortOrder
          : criteria.sortOrder,
      baseQuery: criteria?.baseQuery ?? defaultDevicesSearchCriteria.baseQuery,
      filters: criteria?.filters ?? defaultDevicesSearchCriteria.filters,
    }),
    [defaultDevicesSearchCriteria],
  );

  const apiValueFromConverter = useCallback(
    (value: Date) => moment.utc(value).format(Constants.DATE_TIME_FORMAT_GET),
    [],
  );

  const apiValueToConverter = useCallback(
    (value: Date) => moment.utc(value).format(Constants.DATE_TIME_FORMAT_GET),
    [],
  );

  const devicesFilterConfig = useMemo(
    () => [
      {
        fieldName: "systemId",
        label: t("devicesAtPlanogram.systemId", "ID"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "system_id",
      },
      {
        fieldName: "externalSystemId",
        label: t("devicesAtPlanogram.externalSystemId", "ID EXT"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "external_system_id",
      },
      {
        fieldName: "name",
        label: t("devicesAtPlanogram.name"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "name",
      },
      {
        fieldName: "location",
        label: t("devicesAtPlanogram.location"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "location_name",
      },
      {
        fieldName: "tid",
        label: t("devicesAtPlanogram.tid"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "tid",
      },
      {
        fieldName: "planogramAssignments.assignedDate",
        label: t("devicesAtPlanogram.assigneddate"),
        type: FILTER_TYPES.DATE_TIME_RANGE,
        apiParamName: "assigned_date",
        apiValueFromConverter,
        apiValueToConverter,
      },
      {
        fieldName: "healthState",
        label: t("devicesAtPlanogram.status"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "health_states",
        apiValueConverter: (value: unknown) => {
          if (Array.isArray(value)) {
            return value
              .map((item) => {
                if (item && typeof item === "object" && "id" in item) {
                  return String((item as { id: unknown }).id);
                }

                return String(item);
              })
              .filter(Boolean)
              .join(",");
          }

          return value;
        },
      },
    ],
    [apiValueFromConverter, apiValueToConverter, t],
  );

  const fetchDevicesData = useCallback(
    async (
      criteria: SearchCriteria,
    ): Promise<SubtableFetchResult<DeviceRow>> => {
      if (!planogramKey) {
        return { data: [], totalRecords: 0 };
      }

      try {
        /*
         * The /devices/planogram/{id} endpoint currently accepts
         * filter query parameters but does not apply them. Use the
         * same /devices endpoint as the main Devices table and add a
         * permanent planogram_ids filter so both filtering and
         * planogram membership are handled by the working endpoint.
         */
        const normalizedCriteria = normalizeDevicesCriteria(criteria);

        const planogramFilterValue = [
          {
            id: planogramKey,
            label: planogram.name || planogram.systemId || planogramKey,
          },
        ];

        const requestCriteria: SearchCriteria = {
          ...normalizedCriteria,
          showOnlyActive: false,
          filters: {
            ...(normalizedCriteria.filters ?? {}),
            "planogram.name": {
              type: FILTER_TYPES.LIST,
              filterNameLabel: t("devices.planogramName", "Planogram"),
              value: planogramFilterValue,
              apiParamName: "planogram_ids",
              apiValueConverter: (value: unknown): string => {
                if (!Array.isArray(value)) {
                  return planogramKey;
                }

                return value
                  .map((item) => {
                    if (item && typeof item === "object" && "id" in item) {
                      return String(
                        (
                          item as {
                            id: unknown;
                          }
                        ).id,
                      );
                    }

                    return String(item);
                  })
                  .filter(Boolean)
                  .join(",");
              },
            },
          },
        };

        const response = await DeviceService.getList(requestCriteria);

        const responseData: DeviceListApiRow[] = Array.isArray(response?.data)
          ? response.data
          : [];

        const normalizedData = responseData
          /*
           * The API planogram_ids filter should already guarantee
           * membership. Keep this defensive check for responses
           * that include planogram metadata but ignore that query.
           */
          .filter((device) => {
            const devicePlanogramKey = getDeviceListPlanogramKey(device);

            return !devicePlanogramKey || devicePlanogramKey === planogramKey;
          })
          .map(normalizeDeviceListRow)
          .filter((device) => !removedDeviceIds.has(getDeviceKey(device)));

        return {
          data: normalizedData,
          totalRecords: normalizedData.length,
        };
      } catch (err) {
        console.log("DeviceService::fetch devices at planogram err", err);
        showErrorMessage(t("devicesAtPlanogram.error.fetch"));
        return { data: [], totalRecords: 0 };
      }
    },
    [
      planogram,
      planogramKey,
      normalizeDevicesCriteria,
      removedDeviceIds,
      showErrorMessage,
      t,
    ],
  );

  const removeDeviceFromPlanogram = useCallback(
    async (row: DeviceRow): Promise<void> => {
      const deviceId = getDeviceKey(row);

      if (!planogramKey || !deviceId || removingDeviceId) return;

      setRemovingDeviceId(deviceId);

      try {
        await DeviceService.deleteDeviceListFromPlanogram(
          devicesSearchCriteria,
          planogramKey,
          false,
          [deviceId],
        );

        showSuccessMessage(
          t("devicesAtPlanogram.deleteSelectedDialog.successMessage"),
        );

        setRemovedDeviceIds((prev) => {
          const next = new Set(prev);
          next.add(deviceId);
          return next;
        });

        tableRef.current?.refresh();
        onAfterRemoveDevice(planogram);
      } catch {
        showErrorMessage(
          t("devicesAtPlanogram.deleteSelectedDialog.errorMessage"),
        );
      } finally {
        setRemovingDeviceId(null);
      }
    },
    [
      devicesSearchCriteria,
      planogramKey,
      removingDeviceId,
      showSuccessMessage,
      showErrorMessage,
      t,
      onAfterRemoveDevice,
      planogram,
    ],
  );

  const closeDeleteDeviceDialog = useCallback((): void => {
    setDeleteDeviceDialogState({
      visible: false,
      device: null,
    });
  }, []);

  const requestRemoveDeviceFromPlanogram = useCallback(
    (row: DeviceRow): void => {
      setDeleteDeviceDialogState({
        visible: true,
        device: row,
      });
    },
    [],
  );

  const confirmRemoveDeviceFromPlanogram =
    useCallback(async (): Promise<void> => {
      const device = deleteDeviceDialogState.device;

      closeDeleteDeviceDialog();

      if (!device) return;

      await removeDeviceFromPlanogram(device);
    }, [
      deleteDeviceDialogState.device,
      closeDeleteDeviceDialog,
      removeDeviceFromPlanogram,
    ]);

  return (
    <>
      <ConfirmDialog
        visible={deleteDeviceDialogState.visible}
        onHide={closeDeleteDeviceDialog}
        onCancel={closeDeleteDeviceDialog}
        onConfirm={confirmRemoveDeviceFromPlanogram}
        title={t("devicesAtPlanogram.deleteSelectedDialog.title")}
        messageHeader={
          planogram?.name
            ? () => (
                <div className="confirm-dialog-default-header">
                  {planogram.name}
                </div>
              )
            : null
        }
        message={t("devicesAtPlanogram.deleteSelectedDialog.message", {
          count: 1,
        })}
        actionButtonLabel={t(
          "devicesAtPlanogram.deleteSelectedDialog.confirmButtonLabel",
        )}
        variant="danger"
        delayedConfirm
      >
        {null}
      </ConfirmDialog>

      <div className="data-table-subtable-expansion planogram-devices-expansion">
        <DataTableSubtable<DeviceRow, SearchCriteria>
          key={`devices-${planogramKey}`}
          ref={tableRef}
          fetchData={fetchDevicesData}
          searchCriteria={devicesSearchCriteria}
          setSearchCriteria={setDevicesSearchCriteria}
          filterConfig={devicesFilterConfig}
          dataKey="id"
          className="planogram-devices-subtable"
        >
          {/*
                        Column widths are intentionally fixed to keep the
                        expanded device table aligned with the Figma layout.
                    */}
          <Column
            field="systemId"
            header={t("devicesAtPlanogram.systemId", "ID")}
            sortable
            sortField="systemId"
            body={(row: DeviceRow) => row.systemId || row.id || "-"}
            style={{ width: "9%", minWidth: "0" }}
          />

          <Column
            field="externalSystemId"
            header={t("devicesAtPlanogram.externalSystemId", "ID EXT")}
            sortable
            sortField="externalSystemId"
            body={(row: DeviceRow) => row.externalSystemId || "-"}
            style={{ width: "10%", minWidth: "0" }}
          />

          <Column
            field="name"
            header={t("devicesAtPlanogram.name")}
            sortable
            sortField="name"
            style={{ width: "16%", minWidth: "0" }}
          />

          <Column
            field="location"
            header={t("devicesAtPlanogram.location")}
            sortable
            sortField="location"
            style={{ width: "21%", minWidth: "0" }}
          />

          <Column
            field="tid"
            header={t("devicesAtPlanogram.tid")}
            sortable
            sortField="tid"
            style={{ width: "11%", minWidth: "0" }}
          />

          <Column
            field="planogramAssignments.assignedDate"
            header={t("devicesAtPlanogram.assigneddate")}
            sortable
            sortField="planogramAssignments.assignedDate"
            body={(row: DeviceRow) =>
              RenderedHelper.formatTimestamp(getAssignedDate(row))
            }
            style={{ width: "15%", minWidth: "0" }}
          />

          <Column
            field="healthState"
            header={t("devicesAtPlanogram.status")}
            sortable
            sortField="healthState"
            className="data-table-subtable-status-column planogram-devices-status-column"
            headerClassName="data-table-subtable-status-column planogram-devices-status-column"
            bodyClassName="data-table-subtable-status-column planogram-devices-status-column"
            body={(row: DeviceRow) =>
              RenderedHelper.formatEnum(row.healthState, "DeviceHealthState")
            }
            style={{ width: "10%", minWidth: "0" }}
          />

          <Column
            field="actions"
            header=" "
            className="data-table-subtable-actions-column planogram-devices-actions-column"
            headerClassName="data-table-subtable-actions-column planogram-devices-actions-column"
            bodyClassName="data-table-subtable-actions-column planogram-devices-actions-column"
            body={(row: DeviceRow) => {
              const deviceId = getDeviceKey(row);
              const isRemoving = removingDeviceId === deviceId;
              const deviceDetailsUrl = `/devices/${deviceId}?${navigationContext.getContextParam()}`;

              return (
                <div className="data-table-subtable-row-actions planogram-devices-row-actions">
                  {canWrite && (
                    <button
                      type="button"
                      className="data-table-subtable-remove-button planogram-devices-remove-button"
                      disabled={isRemoving}
                      title={t("devicesAtPlanogram.deleteSelected", "Remove")}
                      aria-label={t(
                        "devicesAtPlanogram.deleteSelected",
                        "Remove",
                      )}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        requestRemoveDeviceFromPlanogram(row);
                      }}
                    >
                      <RemoveLineRedBgIcon className="data-table-subtable-remove-icon planogram-devices-remove-icon" />
                    </button>
                  )}

                  <button
                    type="button"
                    className="data-table-subtable-navigate-button planogram-devices-navigate-button"
                    disabled={!deviceId}
                    title={t("devicesAtPlanogram.view", "View")}
                    aria-label={t("devicesAtPlanogram.view", "View")}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();

                      if (!deviceId) return;

                      navigate(deviceDetailsUrl);
                    }}
                  >
                    <ArrowDetailsIcon className="data-table-subtable-navigate-icon planogram-devices-navigate-icon" />
                  </button>
                </div>
              );
            }}
            style={{ width: "80px", minWidth: "80px", maxWidth: "80px" }}
          />
        </DataTableSubtable>

        {canWrite && (
          <button
            type="button"
            className="data-table-subtable-add-button planogram-devices-add-button"
            onClick={() => onAddDevice(planogram)}
          >
            <AddCircleDarkIcon className="data-table-subtable-add-icon planogram-devices-add-icon" />
            <span>
              {t("devicesAtPlanogram.addNextDevice", "Add next device")}
            </span>
          </button>
        )}
      </div>
    </>
  );
};

const Planograms = (): React.ReactElement => {
  return (
    <DataTableProvider>
      <PlanogramsImpl />
    </DataTableProvider>
  );
};

const PlanogramsImpl = (): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const planogramsTableRef = useRef<any>(null);
  const planogramPendingsTableRef = useRef<any>(null);
  const menuRef = useRef<any>(null);
  const isMounted = useRef(false);

  const [statusOptions, setStatusOptions] = useState<SelectOption[]>([]);
  const [statusPendingsOptions, setStatusPendingsOptions] = useState<
    SelectOption[]
  >([]);

  const { showErrorMessage, showSuccessMessage } = useContext(
    MessageContext,
  ) as MessageContextValue;

  const { isUserInAnyRole } = useAuth();
  const location = useLocation();

  const tabId = PLANOGRAM_TAB_INDEX_BY_ROUTE[location.pathname] ?? 0;

  const [expandedRows, setExpandedRows] = useState<any>(null);

  const isPlanogramExpanded = useCallback(
    (row: PlanogramRow): boolean => {
      const rowKey = getPlanogramKey(row);

      if (!rowKey || !expandedRows) return false;

      if (Array.isArray(expandedRows)) {
        return expandedRows.some(
          (expandedRow: PlanogramRow) =>
            getPlanogramKey(expandedRow) === rowKey,
        );
      }

      return Boolean(expandedRows[rowKey]);
    },
    [expandedRows],
  );

  const togglePlanogramExpansion = useCallback((row: PlanogramRow): void => {
    const rowKey = getPlanogramKey(row);
    const hasAssignedDevices = Number(row?.tiedDevicesCount || 0) > 0;

    if (!rowKey || !hasAssignedDevices) return;

    setExpandedRows((prev: any) => {
      const nextExpandedRows = prev && !Array.isArray(prev) ? { ...prev } : {};

      if (nextExpandedRows[rowKey]) {
        delete nextExpandedRows[rowKey];
      } else {
        nextExpandedRows[rowKey] = true;
      }

      return Object.keys(nextExpandedRows).length ? nextExpandedRows : null;
    });
  }, []);

  const handlePlanogramExpanderClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, row: PlanogramRow): void => {
      event.preventDefault();
      event.stopPropagation();

      togglePlanogramExpansion(row);
    },
    [togglePlanogramExpansion],
  );

  const handlePlanogramRowClick = useCallback(
    (event: any): void => {
      const target = event?.originalEvent?.target as HTMLElement | null;

      // Do not expand/collapse when clicking buttons, menus, filters, etc.
      if (
        target?.closest(
          'button, a, input, textarea, select, [role="button"], .p-checkbox, .p-dropdown, .p-multiselect, .p-column-filter, .p-menu, .p-menuitem',
        )
      ) {
        return;
      }

      togglePlanogramExpansion(event.data as PlanogramRow);
    },
    [togglePlanogramExpansion],
  );

  const [deviceTableRefreshByPlanogram, setDeviceTableRefreshByPlanogram] =
    useState<Record<string, number>>({});

  const [showCreatePlanogramDialog, setShowCreatePlanogramDialog] =
    useState(false);
  const [showAddDeviceToPlanogramDialog, setShowAddDeviceToPlanogramDialog] =
    useState(false);
  const [addDevicePlanogram, setAddDevicePlanogram] =
    useState<PlanogramRow | null>(null);
  const [isOpeningAddDeviceDialog, setIsOpeningAddDeviceDialog] =
    useState(false);

  const [deletePlanogramDialogState, setDeletePlanogramDialogState] = useState<{
    visible: boolean;
    planogramId: string | null;
  }>({
    visible: false,
    planogramId: null,
  });

  const [deactivateState, setDeactivateState] = useState<{
    visible: boolean;
    type: "confirm" | "dialog" | null;
    planogram: PlanogramRow | null;
  }>({
    visible: false,
    type: null,
    planogram: null,
  });

  const [activateDialogState, setActivateDialogState] = useState<{
    visible: boolean;
    planogramId: string | null;
    planogramName: string | null;
  }>({
    visible: false,
    planogramId: null,
    planogramName: null,
  });

  const [duplicatePlanogramDialogState, setDuplicatePlanogramDialogState] =
    useState<{ visible: boolean; planogramId: string | null }>({
      visible: false,
      planogramId: null,
    });

  const [
    deletePlanogramPendingDialogState,
    setDeletePlanogramPendingDialogState,
  ] = useState<{ visible: boolean; planogramPendingId: string | null }>({
    visible: false,
    planogramPendingId: null,
  });

  const [
    deleteSelectedPlanogramsDialogState,
    setDeleteSelectedPlanogramsDialogState,
  ] = useState({ visible: false });

  const [selectedPlanogramCount, setSelectedPlanogramCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const defaultSearchCriteria = useMemo<SearchCriteria>(
    () => ({
      first: 0,
      rows: 25,
      sortField: "createdAt",
      sortOrder: -1,
      baseQuery: "",
      filters: {},
      showOnlyActive: false,
    }),
    [],
  );

  const {
    searchCriteria: listSearchCriteria,
    setSearchCriteria: setListSearchCriteria,
  } = useContext(DataTableContext) as DataTableContextValue;

  const [pendingsSearchCriteria, setPendingsSearchCriteria] =
    useState<SearchCriteria>(defaultSearchCriteria);

  const apiValueFromConverter = useMemo(
    () => (value: Date) =>
      moment.utc(value).format(Constants.DATE_TIME_FORMAT_GET),
    [],
  );

  const apiValueToConverter = useMemo(
    () => (value: Date) =>
      moment.utc(value).format(Constants.DATE_TIME_FORMAT_GET),
    [],
  );

  const navigationContext = useNavigationContext((params: any) => {
    setListSearchCriteria(
      params?.listSearchCriteria
        ? params.listSearchCriteria
        : defaultSearchCriteria,
    );

    setPendingsSearchCriteria(
      params?.pendingsSearchCriteria
        ? params.pendingsSearchCriteria
        : defaultSearchCriteria,
    );
  });

  const navigationContextRef = useRef(navigationContext);

  useEffect(() => {
    navigationContextRef.current = navigationContext;
  }, [navigationContext]);

  useEffect(() => {
    const currentNavigationContext = navigationContextRef.current;

    currentNavigationContext.putParam("listSearchCriteria", listSearchCriteria);
    currentNavigationContext.putParam(
      "pendingsSearchCriteria",
      pendingsSearchCriteria,
    );
  }, [listSearchCriteria, pendingsSearchCriteria]);

  const fetchPlanogramStatusOptions = useCallback((): void => {
    PlanogramService.getStatusList()
      .then((statuses: string[]) => {
        if (!isMounted.current) return;

        setStatusOptions(
          statuses.map((status) => ({
            id: status,
            label: t(`planogramsPage.status.${status.toLowerCase()}`),
          })),
        );
      })
      .catch(() => {
        showErrorMessage(t("planogramsPage.error.fetchStatuses"));
      });
  }, [showErrorMessage, t]);

  const fetchPlanogramPendingsStatusOptions = useCallback((): void => {
    PlanogramPendingService.getStatusList()
      .then((statuses: string[]) => {
        if (!isMounted.current) return;

        setStatusPendingsOptions(
          statuses.map((status) => ({
            id: status,
            label: t(`PlanogramPendingStatus.${status}`),
          })),
        );
      })
      .catch(() => {
        showErrorMessage(t("planogramsPage.error.fetchStatuses"));
      });
  }, [showErrorMessage, t]);

  useEffect(() => {
    isMounted.current = true;

    fetchPlanogramStatusOptions();
    fetchPlanogramPendingsStatusOptions();

    return () => {
      isMounted.current = false;
    };
  }, [fetchPlanogramStatusOptions, fetchPlanogramPendingsStatusOptions]);

  useEffect(() => {
    if (!isMounted.current) return;

    fetchPlanogramStatusOptions();
    fetchPlanogramPendingsStatusOptions();
  }, [
    i18n.language,
    fetchPlanogramStatusOptions,
    fetchPlanogramPendingsStatusOptions,
  ]);

  const openAddDeviceDialog = useCallback(
    async (planogram: PlanogramRow): Promise<void> => {
      const planogramKey = getPlanogramKey(planogram);

      if (!planogramKey || isOpeningAddDeviceDialog) return;

      setIsOpeningAddDeviceDialog(true);
      setShowAddDeviceToPlanogramDialog(false);
      setAddDevicePlanogram(null);

      try {
        // Important:
        // The list row does not contain the same normalized planogram data
        // that PlanogramDetails passes to CopyPlanogramToDeviceDialog.
        // Fetch details first, normalize cells to the editor grid shape, and
        // then open the dialog so its locations/devices requests are made
        // with the same payload shape as PlanogramDetails.
        const details = await PlanogramService.getDetails(planogramKey);
        const fullPlanogram = normalizePlanogramForCopyDialog(
          planogram,
          details,
          planogramKey,
        );

        setAddDevicePlanogram(fullPlanogram);
        setShowAddDeviceToPlanogramDialog(true);
      } catch (error) {
        console.error(
          "Planograms::openAddDeviceDialog getDetails error:",
          error,
        );
        showErrorMessage(t("planogram.error.fetch"));
      } finally {
        setIsOpeningAddDeviceDialog(false);
      }
    },
    [isOpeningAddDeviceDialog, showErrorMessage, t],
  );

  const planogramsFilterConfig = useMemo(
    () => [
      {
        fieldName: "systemId",
        label: t("planogramsPage.systemId"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "system_id",
      },
      {
        fieldName: "planogramId",
        label: t("planogramsPage.externalSystemId"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "external_system_id",
      },
      {
        fieldName: "name",
        label: t("planogramsPage.columns.name"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "name",
      },
      {
        fieldName: "createdAt",
        label: t("planogramsPage.columns.createdAt"),
        type: FILTER_TYPES.DATE_TIME_RANGE,
        apiParamName: "created_at",
        apiValueFromConverter,
        apiValueToConverter,
      },
      {
        fieldName: "status",
        label: t("planogramsPage.columns.status"),
        type: FILTER_TYPES.LIST,
        params: statusOptions,
        apiParamName: "statuses",
        apiValueConverter: (value: SelectOption[]) =>
          value.map((item) => item.id),
      },
      {
        fieldName: "tiedDevicesCount",
        label: t("planogramsPage.columns.tiedDevices"),
        type: FILTER_TYPES.NUMBER_RANGE,
        params: { maxValue: 100 },
        apiParamName: "tied_devices_count",
      },
      {
        fieldName: "rowsCount",
        label: t("planogramsPage.columns.rows"),
        type: FILTER_TYPES.NUMBER_RANGE,
        params: { maxValue: 50 },
        apiParamName: "rows",
      },
      {
        fieldName: "columnsCount",
        label: t("planogramsPage.columns.columns"),
        type: FILTER_TYPES.NUMBER_RANGE,
        params: { maxValue: 50 },
        apiParamName: "cols",
      },
    ],
    [statusOptions, t, apiValueFromConverter, apiValueToConverter],
  );

  const planogramPendingsFilterConfig = useMemo(
    () => [
      {
        fieldName: "oldPlanogramPendings.name",
        label: t("planogramsPage.pendings.columns.oldPlanogramName"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "old_planogram_pendings_name",
      },
      {
        fieldName: "name",
        label: t("planogramsPage.pendings.columns.newPlanogramName"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "name",
      },
      {
        fieldName: "device.name",
        label: t("planogramsPage.pendings.columns.device"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "device_name",
      },
      {
        fieldName: "location.name",
        label: t("planogramsPage.pendings.columns.location"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "location_name",
      },
      {
        fieldName: "route.name",
        label: t("planogramsPage.pendings.columns.routeId"),
        type: FILTER_TYPES.TEXT,
        apiParamName: "route_name",
      },
      {
        fieldName: "implementationDate",
        label: t("planogramsPage.pendings.columns.implementationDate"),
        type: FILTER_TYPES.DATE_RANGE,
        apiParamName: "implementation_date",
        apiValueFromConverter,
        apiValueToConverter,
      },
      {
        fieldName: "status",
        label: t("planogramsPage.columns.status"),
        type: FILTER_TYPES.LIST,
        params: statusPendingsOptions,
        apiParamName: "statuses",
        apiValueConverter: (value: SelectOption[]) =>
          value.map((item) => item.id),
      },
    ],
    [statusPendingsOptions, t, apiValueFromConverter, apiValueToConverter],
  );

  const normalizePlanogramCriteria = useCallback(
    (criteria?: Partial<SearchCriteria> | null): SearchCriteria => ({
      ...defaultSearchCriteria,
      ...criteria,
      first: criteria?.first ?? defaultSearchCriteria.first,
      rows: criteria?.rows ?? defaultSearchCriteria.rows,
      sortField:
        !criteria?.sortField || criteria.sortField === "null"
          ? defaultSearchCriteria.sortField
          : criteria.sortField,
      sortOrder:
        criteria?.sortOrder === null ||
        criteria?.sortOrder === undefined ||
        criteria?.sortOrder === 0
          ? -1
          : criteria.sortOrder,
      baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
      filters: criteria?.filters ?? defaultSearchCriteria.filters,
      showOnlyActive:
        criteria?.showOnlyActive ?? defaultSearchCriteria.showOnlyActive,
    }),
    [defaultSearchCriteria],
  );

  const normalizePlanogramPendingsCriteria = useCallback(
    (criteria?: Partial<SearchCriteria> | null): SearchCriteria => ({
      ...defaultSearchCriteria,
      ...criteria,
      first: criteria?.first ?? defaultSearchCriteria.first,
      rows: criteria?.rows ?? defaultSearchCriteria.rows,
      sortField:
        !criteria?.sortField || criteria.sortField === "null"
          ? "implementationDate"
          : criteria.sortField,
      sortOrder:
        criteria?.sortOrder === null ||
        criteria?.sortOrder === undefined ||
        criteria?.sortOrder === 0
          ? -1
          : criteria.sortOrder,
      baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
      filters: criteria?.filters ?? defaultSearchCriteria.filters,
    }),
    [defaultSearchCriteria],
  );

  const fetchPlanogramsData = useCallback(
    async (criteria: Partial<SearchCriteria>) => {
      try {
        return await PlanogramService.getList(
          normalizePlanogramCriteria(criteria),
        );
      } catch (err) {
        console.log("PlanogramService::fetch err", err);
        showErrorMessage(t("planogramsPage.error.fetch"));
        return { data: [], totalRecords: 0 };
      }
    },
    [normalizePlanogramCriteria, showErrorMessage, t],
  );

  const fetchPlanogramPendingsData = useCallback(
    async (criteria: Partial<SearchCriteria>) => {
      try {
        return await PlanogramPendingService.getList(
          normalizePlanogramPendingsCriteria(criteria),
        );
      } catch (err) {
        console.log("PlanogramPendingService::fetch err", err);
        showErrorMessage(t("planogramsPage.error.fetchPendings"));
        return { data: [], totalRecords: 0 };
      }
    },
    [normalizePlanogramPendingsCriteria, showErrorMessage, t],
  );

  const deleteSelectedPlanograms = (): void => {
    setDeleteSelectedPlanogramsDialogState({ visible: true });
  };

  const confirmDeleteSelectedPlanograms = async (): Promise<void> => {
    setIsDeleting(true);

    try {
      await PlanogramService.deleteList(
        listSearchCriteria,
        planogramsTableRef.current?.selectedAll,
        planogramsTableRef.current?.selectionExceptions,
      );

      showSuccessMessage(t("planogramsPage.success.delete"));
      planogramsTableRef.current?.refresh({ clearSelections: true });
    } catch {
      showErrorMessage(t("planogramsPage.error.delete"));
    } finally {
      setIsDeleting(false);
      setDeleteSelectedPlanogramsDialogState({ visible: false });
    }
  };

  const planogramsTableToolbarRight = (
    <Button
      className="small"
      onClick={() => setShowCreatePlanogramDialog(true)}
    >
      <AddCircleIcon />
      <span className="p-button-label pl-2">{t("planogramsPage.addNew")}</span>
    </Button>
  );

  const planogramsTableToolbarBottom = (
    <div className="flex align-items-center gap-2">
      {selectedPlanogramCount > 0 && (
        <>
          <span className="selected-label">
            {t("planogramsPage.selected", {
              count: selectedPlanogramCount,
            })}
          </span>

          <Button
            className="small"
            severity="danger"
            onClick={deleteSelectedPlanograms}
            loading={isDeleting}
          >
            <TrashIcon className="icon20" />
            <span className="p-button-label pl-2">
              {t("planogramsPage.deleteSelected")}
            </span>
          </Button>
        </>
      )}
    </div>
  );

  const generatePlanogramsMenuItems = (row: PlanogramRow): any[] => {
    const items: any[] = [];

    if (isUserInAnyRole(["PLANOGRAMS_WRITE"])) {
      items.push({
        label: t("planogramsPage.menu.edit"),
        icon: "edit",
        key: `edit-${row.id}`,
        command: () => {
          navigate(`/planograms/edit/${row.id}/visual`);
        },
      });
    }

    if (isUserInAnyRole(["PLANOGRAMS_WRITE"])) {
      items.push({
        label: t("planogramsPage.menu.duplicate"),
        icon: "duplicate",
        key: `duplicate-${row.id}`,
        command: () => {
          setDuplicatePlanogramDialogState({
            visible: true,
            planogramId: row.id || null,
          });
        },
      });
    }

    if (isUserInAnyRole(["PLANOGRAMS_WRITE"])) {
      if (row.status === "DEACTIVATED") {
        items.push({
          label: t("planogramsPage.menu.activate"),
          icon: "activate",
          separatorBefore: true,
          key: `activate-${row.id}`,
          command: () => {
            setActivateDialogState({
              visible: true,
              planogramId: row.id || null,
              planogramName: row.name || null,
            });
          },
        });
      } else {
        items.push({
          label: t("planogramsPage.menu.deactivate"),
          icon: "deactivate",
          separatorBefore: true,
          key: `deactivate-${row.id}`,
          command: () => {
            if (row.tiedDevicesCount && row.tiedDevicesCount > 0) {
              setDeactivateState({
                visible: true,
                type: "dialog",
                planogram: row,
              });
            } else {
              setDeactivateState({
                visible: true,
                type: "confirm",
                planogram: row,
              });
            }
          },
        });
      }
    }

    if (isUserInAnyRole(["PLANOGRAMS_WRITE"]) && row.status === "DEACTIVATED") {
      items.push({
        label: t("planogramsPage.menu.delete"),
        icon: "delete",
        separatorBefore: true,
        key: `delete-${row.id}`,
        command: () => {
          setDeletePlanogramDialogState({
            visible: true,
            planogramId: row.id || null,
          });
        },
      });
    }

    return items;
  };

  const deletePlanogram = async (): Promise<void> => {
    try {
      await PlanogramService.delete(deletePlanogramDialogState.planogramId);
      showSuccessMessage(t("planogramsPage.success.delete"));
      planogramsTableRef.current?.refresh();
    } catch {
      showErrorMessage(t("planogramsPage.error.delete"));
    }

    setDeletePlanogramDialogState({
      visible: false,
      planogramId: null,
    });
  };

  const duplicatePlanogram = async (): Promise<void> => {
    try {
      await PlanogramService.duplicate(
        duplicatePlanogramDialogState.planogramId,
      );
      showSuccessMessage(t("planogramsPage.success.duplicate"));
      planogramsTableRef.current?.refresh();
    } catch {
      showErrorMessage(t("planogramsPage.error.duplicate"));
    }

    setDuplicatePlanogramDialogState({
      visible: false,
      planogramId: null,
    });
  };

  const activatePlanogram = async (): Promise<void> => {
    try {
      await PlanogramService.activate(activateDialogState.planogramId);
      showSuccessMessage(t("planogramsPage.activateDialog.successMessage"));
      planogramsTableRef.current?.refresh();
    } catch {
      showErrorMessage(t("planogramsPage.activateDialog.errorMessage"));
    }

    setActivateDialogState({
      visible: false,
      planogramId: null,
      planogramName: null,
    });
  };

  const deletePlanogramPending = async (): Promise<void> => {
    try {
      await PlanogramPendingService.delete(
        deletePlanogramPendingDialogState.planogramPendingId,
      );
      showSuccessMessage(t("planogramsPage.success.delete"));
      planogramPendingsTableRef.current?.refresh();
    } catch {
      showErrorMessage(t("planogramsPage.error.delete"));
    }

    setDeletePlanogramPendingDialogState({
      visible: false,
      planogramPendingId: null,
    });
  };

  const generatePlanogramPendingsMenuItems = (row: any): any[] => {
    const items: any[] = [];

    if (
      isUserInAnyRole(["PLANOGRAMS_WRITE"]) &&
      (row.status === "TO_BE_DONE" || row.status === "PLANNED")
    ) {
      items.push({
        label: t("planogramsPage.menu.markAsDone"),
        icon: "markAsDone",
        separatorBefore: false,
        key: `mark-as-done-${row.id}`,
        command: async () => {
          try {
            await PlanogramPendingService.markAsDone(row.id);
            showSuccessMessage(t("planogramsPage.success.markAsDone"));
            planogramPendingsTableRef.current?.refresh();
          } catch {
            showErrorMessage(t("planogramsPage.error.markAsDone"));
          }
        },
      });
    }

    if (isUserInAnyRole(["PLANOGRAMS_WRITE"])) {
      items.push({
        label: t("planogramsPage.menu.delete"),
        icon: "delete",
        separatorBefore: false,
        key: `delete-${row.id}`,
        command: () => {
          setDeletePlanogramPendingDialogState({
            visible: true,
            planogramPendingId: row.id,
          });
        },
      });
    }

    return items;
  };

  const renderAssignedDevicesExpansion = useCallback(
    (planogram: PlanogramRow): React.ReactNode => {
      const planogramKey = getPlanogramKey(planogram);

      return (
        <InlineDevicesTable
          planogram={planogram}
          refreshToken={deviceTableRefreshByPlanogram[planogramKey]}
          canWrite={isUserInAnyRole(["PLANOGRAMS_WRITE"])}
          navigationContext={navigationContext}
          onAddDevice={openAddDeviceDialog}
          onAfterRemoveDevice={(updatedPlanogram) => {
            const updatedPlanogramKey = getPlanogramKey(updatedPlanogram);

            setDeviceTableRefreshByPlanogram((prev) => ({
              ...prev,
              [updatedPlanogramKey]: Date.now(),
            }));

            if ((updatedPlanogram.tiedDevicesCount || 0) <= 1) {
              setExpandedRows((prev: any) => {
                if (!prev || Array.isArray(prev)) return prev;

                const nextExpandedRows = { ...prev };
                delete nextExpandedRows[updatedPlanogramKey];

                return Object.keys(nextExpandedRows).length
                  ? nextExpandedRows
                  : null;
              });
            }

            planogramsTableRef.current?.refresh();
          }}
          showErrorMessage={showErrorMessage}
          showSuccessMessage={showSuccessMessage}
        />
      );
    },
    [
      deviceTableRefreshByPlanogram,
      isUserInAnyRole,
      navigationContext,
      openAddDeviceDialog,
      showErrorMessage,
      showSuccessMessage,
    ],
  );

  const addDevicePlanogramRows = useMemo(
    () => getPlanogramRows(addDevicePlanogram),
    [addDevicePlanogram],
  );

  const addDevicePlanogramColumns = useMemo(
    () => getPlanogramColumns(addDevicePlanogram),
    [addDevicePlanogram],
  );

  const addDevicePlanogramDialogData = useMemo<PlanogramRow | null>(() => {
    if (!addDevicePlanogram) return null;

    return {
      ...addDevicePlanogram,
      rows: addDevicePlanogramRows ?? undefined,
      columns: addDevicePlanogramColumns ?? undefined,
    };
  }, [addDevicePlanogram, addDevicePlanogramRows, addDevicePlanogramColumns]);

  const addDevicePlanogramExternalIdToExclude = useMemo(() => {
    if (!addDevicePlanogramDialogData) return null;

    return (
      getStringValue(addDevicePlanogramDialogData.externalId) ??
      getStringValue(addDevicePlanogramDialogData.uuid) ??
      getStringValue(addDevicePlanogramDialogData.id)
    );
  }, [addDevicePlanogramDialogData]);

  const isAddDevicePlanogramComplete = useCallback(
    () => isPlanogramCompleteUtil(addDevicePlanogramDialogData as any),
    [addDevicePlanogramDialogData],
  );

  const renderPlanogramListDialogs = (): React.ReactNode => {
    const closeDeactivateDialog = () =>
      setDeactivateState({
        visible: false,
        type: null,
        planogram: null,
      });

    const closeDeletePlanogramDialog = () =>
      setDeletePlanogramDialogState({
        visible: false,
        planogramId: null,
      });

    const closeDuplicatePlanogramDialog = () =>
      setDuplicatePlanogramDialogState({
        visible: false,
        planogramId: null,
      });

    const closeDeleteSelectedPlanogramsDialog = () =>
      setDeleteSelectedPlanogramsDialogState({
        visible: false,
      });

    const closeDeletePlanogramPendingDialog = () =>
      setDeletePlanogramPendingDialogState({
        visible: false,
        planogramPendingId: null,
      });

    const closeActivateDialog = () =>
      setActivateDialogState({
        visible: false,
        planogramId: null,
        planogramName: null,
      });

    return (
      <>
        <ConfirmDialog
          visible={
            deactivateState.visible && deactivateState.type === "confirm"
          }
          onHide={closeDeactivateDialog}
          onCancel={closeDeactivateDialog}
          onConfirm={async () => {
            try {
              await PlanogramService.deactivate(deactivateState.planogram?.id);
              showSuccessMessage(
                t("planogramsPage.deactivateDialog.successMessage"),
              );
              planogramsTableRef.current?.refresh();
            } catch {
              showErrorMessage(
                t("planogramsPage.deactivateDialog.errorMessage"),
              );
            }

            setDeactivateState({
              visible: false,
              type: null,
              planogram: null,
            });
          }}
          title={t("planogramsPage.deactivateDialog.title")}
          messageHeader={() =>
            deactivateState?.planogram?.name ? (
              <div className="confirm-dialog-default-header">
                {deactivateState.planogram.name}
              </div>
            ) : null
          }
          message={t("planogramsPage.deactivateDialog.message")}
          actionButtonLabel={t(
            "planogramsPage.deactivateDialog.confirmButtonLabel",
          )}
          delayedConfirm
        />

        <PlanogramDeactivateDialog
          visible={deactivateState.visible && deactivateState.type === "dialog"}
          onHide={() => {
            setDeactivateState({
              visible: false,
              type: null,
              planogram: null,
            });
            planogramsTableRef.current?.refresh();
          }}
          onConfirm={() => {
            planogramsTableRef.current?.refresh();
          }}
          planogramTitle={deactivateState.planogram?.name}
          planogramRows={deactivateState.planogram?.rowsCount}
          planogramColumns={deactivateState.planogram?.columnsCount}
          planogramId={deactivateState.planogram?.id}
        />

        <ConfirmDialog
          visible={deletePlanogramDialogState.visible}
          onHide={closeDeletePlanogramDialog}
          onCancel={closeDeletePlanogramDialog}
          onConfirm={deletePlanogram}
          title={t("planogramsPage.deleteDialog.title")}
          messageHeader={null}
          message={t("planogramsPage.deleteDialog.message")}
          actionButtonLabel={t(
            "planogramsPage.deleteDialog.confirmButtonLabel",
          )}
          variant="danger"
          delayedConfirm
        />

        <ConfirmDialog
          visible={duplicatePlanogramDialogState.visible}
          onHide={closeDuplicatePlanogramDialog}
          onCancel={closeDuplicatePlanogramDialog}
          onConfirm={duplicatePlanogram}
          title={t("planogramsPage.duplicateDialog.title")}
          messageHeader={null}
          message={t("planogramsPage.duplicateDialog.message")}
          actionButtonLabel={t(
            "planogramsPage.duplicateDialog.confirmButtonLabel",
          )}
          delayedConfirm
        />

        <ConfirmDialog
          visible={deleteSelectedPlanogramsDialogState.visible}
          onHide={closeDeleteSelectedPlanogramsDialog}
          onCancel={closeDeleteSelectedPlanogramsDialog}
          onConfirm={confirmDeleteSelectedPlanograms}
          title={t("planogramsPage.deleteSelectedDialog.title")}
          messageHeader={null}
          message={t("planogramsPage.deleteSelectedDialog.message", {
            count: selectedPlanogramCount,
          })}
          actionButtonLabel={t(
            "planogramsPage.deleteSelectedDialog.confirmButtonLabel",
          )}
          variant="danger"
          delayedConfirm
        />

        <ConfirmDialog
          visible={deletePlanogramPendingDialogState.visible}
          onHide={closeDeletePlanogramPendingDialog}
          onCancel={closeDeletePlanogramPendingDialog}
          onConfirm={deletePlanogramPending}
          title={t("planogramsPage.deleteDialog.title")}
          messageHeader={null}
          message={t("planogramsPage.deleteDialog.message")}
          actionButtonLabel={t(
            "planogramsPage.deleteDialog.confirmButtonLabel",
          )}
          variant="danger"
          delayedConfirm
        />

        <ConfirmDialog
          visible={activateDialogState.visible}
          onHide={closeActivateDialog}
          onCancel={closeActivateDialog}
          onConfirm={activatePlanogram}
          title={t("planogramsPage.activateDialog.title")}
          messageHeader={() =>
            activateDialogState?.planogramName ? (
              <div className="confirm-dialog-default-header">
                {activateDialogState.planogramName}
              </div>
            ) : null
          }
          message={t("planogramsPage.activateDialog.message")}
          actionButtonLabel={t(
            "planogramsPage.activateDialog.confirmButtonLabel",
          )}
          variant="success"
          delayedConfirm
        />

        <CreatePlanogramDialog
          visible={showCreatePlanogramDialog}
          onHide={() => setShowCreatePlanogramDialog(false)}
          handleSubmit={async (formData: any) => {
            try {
              const payload = {
                name: formData.name?.trim(),
                externalSystemId: formData.externalSystemId?.trim() || null,
                rows: formData.rows,
                columns: formData.columns,
              };

              const created = await PlanogramService.create(payload);

              showSuccessMessage(t("planogram.create.success"));
              setShowCreatePlanogramDialog(false);
              planogramsTableRef.current?.refresh();

              if (created?.id) {
                navigate(`/planograms/edit/${created.id}/visual`);
              }
            } catch {
              showErrorMessage(t("planogramsPage.error.create"));
            }
          }}
        />

        <CopyPlanogramToDeviceDialog
          visible={
            showAddDeviceToPlanogramDialog &&
            Boolean(addDevicePlanogramDialogData)
          }
          onHide={() => {
            setShowAddDeviceToPlanogramDialog(false);
            setAddDevicePlanogram(null);
            setIsOpeningAddDeviceDialog(false);
          }}
          onSave={async (data: any) => {
            const planogramKey = getPlanogramKey(addDevicePlanogram);
            if (
              !planogramKey ||
              !addDevicePlanogram ||
              !addDevicePlanogramDialogData
            ) {
              return;
            }

            const planogramUuid =
              addDevicePlanogramDialogData.externalId ??
              addDevicePlanogramDialogData.id ??
              addDevicePlanogramDialogData.uuid ??
              planogramKey;

            const deviceIds = (data?.device ?? [])
              .map(
                (device: SelectOption) =>
                  device.id ?? device.uuid ?? device.externalId,
              )
              .filter(Boolean);

            const planogramToUpdate =
              PlanogramTransformUtils.createApiPlanogram(
                addDevicePlanogramDialogData as any,
              );

            try {
              await PlanogramPendingService.copyToDevicesFromParent(
                planogramUuid,
                planogramToUpdate,
                deviceIds,
                data?.implementationDate ?? null,
                data?.applyImmediately,
              );

              showSuccessMessage(t("planogram.applyToDevice.success"));

              setDeviceTableRefreshByPlanogram((prev) => ({
                ...prev,
                [planogramKey]: Date.now(),
              }));

              planogramsTableRef.current?.refresh();

              setShowAddDeviceToPlanogramDialog(false);
              setAddDevicePlanogram(null);
              setIsOpeningAddDeviceDialog(false);
            } catch (error) {
              console.error(
                "Planograms::copyToDevicesFromParent error:",
                error,
              );
              showErrorMessage(t("planogram.applyToDevice.error"));
            }
          }}
          transformPlanogram
          planogramData={addDevicePlanogramDialogData}
          planogramTitle={addDevicePlanogramDialogData?.name}
          planogramRows={addDevicePlanogramDialogData?.rows}
          planogramColumns={addDevicePlanogramDialogData?.columns}
          isPlanogramComplete={isAddDevicePlanogramComplete}
          defaultValues={null}
          deviceExternalIdToExclude={null}
          planogramExternalIdToExclude={addDevicePlanogramExternalIdToExclude}
          title={t("planogram.copyToDevice.title")}
          incompleteTitle={t("planogram.copyToDevice.incompleteTitle")}
        />
      </>
    );
  };

  const renderPlanogramListDataTable = (): React.ReactNode => {
    return (
      <ExpandableDataTable
        ref={planogramsTableRef}
        fetchData={fetchPlanogramsData}
        filterConfig={planogramsFilterConfig}
        defaultSearchCriteria={defaultSearchCriteria}
        tableToolbarRight={planogramsTableToolbarRight}
        tableToolbarBottom={
          selectedPlanogramCount > 0 ? planogramsTableToolbarBottom : null
        }
        selectable={false}
        onSelectionChange={setSelectedPlanogramCount}
        dataKey="id"
        expandedRows={expandedRows}
        onRowClick={handlePlanogramRowClick}
        onRowToggle={(event: any) => setExpandedRows(event.data)}
        rowExpansionTemplate={renderAssignedDevicesExpansion}
      >
        <Column
          field="systemId"
          header={t("planogramsPage.systemId")}
          sortable
          sortField="systemId"
          key="systemId"
          className="planogram-system-id-column"
          headerClassName="dt2-header-wrap planogram-system-id-column"
          bodyClassName="planogram-system-id-column"
          body={(row: PlanogramRow) => {
            const hasAssignedDevices = Number(row?.tiedDevicesCount || 0) > 0;
            const expanded = isPlanogramExpanded(row);

            return (
              <div className="planogram-system-id-cell">
                {hasAssignedDevices ? (
                  <button
                    type="button"
                    className="data-table-expander-button planogram-expander-button"
                    aria-expanded={expanded}
                    aria-label={
                      expanded
                        ? t("common.collapse", "Collapse")
                        : t("common.expand", "Expand")
                    }
                    title={
                      expanded
                        ? t("common.collapse", "Collapse")
                        : t("common.expand", "Expand")
                    }
                    onClick={(event) =>
                      handlePlanogramExpanderClick(event, row)
                    }
                  >
                    {expanded ? (
                      <KeyboardArrowDownIcon className="data-table-expander-icon planogram-expander-icon" />
                    ) : (
                      <KeyboardArrowUpIcon className="data-table-expander-icon planogram-expander-icon" />
                    )}
                  </button>
                ) : (
                  <span
                    className="data-table-expander-spacer planogram-expander-spacer"
                    aria-hidden="true"
                  />
                )}

                <span className="planogram-system-id-value">
                  {row.systemId || row.id || "-"}
                </span>
              </div>
            );
          }}
          style={{ minWidth: "120px", maxWidth: "140px" }}
        />

        <Column
          field="externalSystemId"
          header={t("planogramsPage.externalSystemId")}
          sortable
          sortField="planogramId"
          key="planogramId"
          headerClassName="dt2-header-wrap"
          style={{ minWidth: "100px", maxWidth: "110px" }}
        />

        <Column
          field="name"
          header={t("planogramsPage.columns.name")}
          sortable
          sortField="name"
          key="name"
          style={{ minWidth: "160px", maxWidth: "200px" }}
        />

        <Column
          field="createdAt"
          header={t("planogramsPage.columns.createdAt")}
          sortable
          key="createdAt"
          headerClassName="dt2-header-wrap"
          body={(row: PlanogramRow) =>
            RenderedHelper.formatTimestamp(row.createdAt)
          }
          style={{ minWidth: "130px", maxWidth: "150px" }}
        />

        <Column
          field="tiedDevicesCount"
          header={t("planogramsPage.columns.tiedDevices")}
          sortable
          sortField="tiedDevicesCount"
          key="tiedDevicesCount"
          headerClassName="dt2-header-wrap"
          style={{ minWidth: "110px", maxWidth: "120px" }}
        />

        <Column
          field="rowsCount"
          header={t("planogramsPage.columns.rows")}
          sortable
          key="rowsCount"
          style={{ minWidth: "90px", maxWidth: "100px" }}
        />

        <Column
          field="columnsCount"
          header={t("planogramsPage.columns.columns")}
          sortable
          key="columnsCount"
          headerClassName="dt2-header-wrap"
          style={{ minWidth: "100px", maxWidth: "120px" }}
        />

        <Column
          field="status"
          header={t("planogramsPage.columns.status")}
          sortable
          sortField="status"
          key="status"
          body={(row: PlanogramRow) =>
            RenderedHelper.formatEnum(row.status, "PlanogramStatus")
          }
          style={{ minWidth: "120px", maxWidth: "150px" }}
        />

        <Column
          className="dt2-actions-column"
          headerClassName="dt2-actions-column"
          bodyClassName="dt2-actions-column"
          body={(row: PlanogramRow) => (
            <ActionButton
              menuRef={menuRef}
              generateMenuItems={() => generatePlanogramsMenuItems(row)}
              showMainNavigation
              mainNavigateTo={`/planograms/view/${row.id}?${navigationContext.getContextParam()}`}
            >
              {null}
            </ActionButton>
          )}
        />
      </ExpandableDataTable>
    );
  };

  const setPlanogramStatusFilter = useCallback(
    (page: number): void => {
      let status: string | null = null;

      switch (page) {
        case 0:
          status = null;
          break;

        case 1:
          status = "PENDING";
          break;

        default:
          status = null;
          break;
      }

      if (page === 0) {
        setListSearchCriteria((previous: SearchCriteria) => {
          const filters = {
            ...(previous?.filters || {}),
          };

          delete filters.status;

          return {
            ...(previous || defaultSearchCriteria),
            status,
            filters,
          };
        });
      }

      if (page === 1) {
        setPendingsSearchCriteria((previous: SearchCriteria) => {
          const filters = {
            ...(previous?.filters || {}),
          };

          delete filters.status;

          return {
            ...(previous || defaultSearchCriteria),
            status,
            filters,
          };
        });
      }
    },
    [defaultSearchCriteria, setListSearchCriteria],
  );

  const handlePlanogramTabChange = useCallback(
    (index: number): void => {
      const route = PLANOGRAM_TAB_ROUTE_BY_INDEX[index];

      if (!route || location.pathname === route) {
        return;
      }

      navigate({
        pathname: route,
        search: location.search,
      });
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    setPlanogramStatusFilter(tabId);
  }, [tabId, setPlanogramStatusFilter]);

  const renderPlanogramTabHeader = useCallback(
    (index: number, label: React.ReactNode) => {
      const route = PLANOGRAM_TAB_ROUTE_BY_INDEX[index];

      return (options: TabHeaderTemplateOptions): React.ReactNode => (
        <NavLink
          to={{
            pathname: route,
            search: location.search,
          }}
          role="tab"
          aria-selected={options.selected}
          aria-controls={options.ariaControls}
          tabIndex={options.selected ? 0 : -1}
          className={options.className}
          onClick={(event) => {
            /*
             * Preserve native browser link actions:
             *
             * - Ctrl/Cmd + click
             * - Shift + click
             * - middle mouse click
             * - browser context menu
             */
            if (isModifiedLinkClick(event)) {
              return;
            }

            event.preventDefault();
            handlePlanogramTabChange(index);
          }}
        >
          {label}
        </NavLink>
      );
    },
    [handlePlanogramTabChange, location.search],
  );

  const renderPlanogramPendingsDataTable = (): React.ReactNode => {
    return (
      <DataTableProvider>
        <DataTable
          ref={planogramPendingsTableRef}
          fetchData={fetchPlanogramPendingsData}
          filterConfig={planogramPendingsFilterConfig}
          defaultSearchCriteria={defaultSearchCriteria}
        >
          <Column
            field="oldPlanogramPendings.name"
            header={t("planogramsPage.pendings.columns.oldPlanogramName")}
            sortable
            sortField="oldPlanogramPendings.name"
            key="oldPlanogramPendings"
            headerClassName="dt2-header-wrap"
            style={{ minWidth: "150px", maxWidth: "180px" }}
          />

          <Column
            field="name"
            header={t("planogramsPage.pendings.columns.newPlanogramName")}
            sortable
            sortField="name"
            key="name"
            headerClassName="dt2-header-wrap"
            style={{ minWidth: "150px", maxWidth: "180px" }}
          />

          <Column
            field="device.name"
            header={t("planogramsPage.pendings.columns.device")}
            sortable
            sortField="device.name"
            key="device"
            style={{ minWidth: "130px", maxWidth: "160px" }}
          />

          <Column
            field="location.name"
            header={t("planogramsPage.pendings.columns.location")}
            sortable
            sortField="location.name"
            key="location"
            style={{ minWidth: "130px", maxWidth: "160px" }}
          />

          <Column
            field="route.name"
            header={t("planogramsPage.pendings.columns.routeId")}
            sortable
            sortField="route.name"
            key="route"
            headerClassName="dt2-header-wrap"
            style={{ minWidth: "120px", maxWidth: "150px" }}
          />

          <Column
            field="implementationDate"
            header={t("planogramsPage.pendings.columns.implementationDate")}
            sortable
            sortField="implementationDate"
            key="implementationDate"
            headerClassName="dt2-header-wrap"
            body={(row: any) =>
              row.implementationDate
                ? RenderedHelper.formatDate(row.implementationDate)
                : "-"
            }
            style={{ minWidth: "130px", maxWidth: "150px" }}
          />

          <Column
            field="status"
            header={t("planogramsPage.pendings.columns.status")}
            sortable
            sortField="status"
            key="status"
            body={(row: any) =>
              RenderedHelper.formatEnum(row.status, "PlanogramPendingStatus")
            }
            style={{ minWidth: "120px", maxWidth: "150px" }}
          />

          <Column
            className="dt2-actions-column"
            headerClassName="dt2-actions-column"
            bodyClassName="dt2-actions-column"
            body={(row: any) => (
              <ActionButton
                menuRef={menuRef}
                generateMenuItems={() =>
                  generatePlanogramPendingsMenuItems(row)
                }
                showMainNavigation
                mainNavigateTo={`/planogram-pendings/details/${row.id}?${navigationContext.getContextParam()}`}
              >
                {null}
              </ActionButton>
            )}
          />
        </DataTable>
      </DataTableProvider>
    );
  };

  return (
    <div className={`planograms-container planograms-container--tab-${tabId}`}>
      <ActionMenu ref={menuRef} />

      <TabView
        className="w-full"
        activeIndex={tabId}
        onTabChange={(event) => {
          handlePlanogramTabChange(event.index);
        }}
      >
        <TabPanel
          header={t("planogramsPage.tabs.list")}
          headerTemplate={renderPlanogramTabHeader(
            0,
            t("planogramsPage.tabs.list"),
          )}
        >
          <div className="data-card">
            {renderPlanogramListDataTable()}
            {renderPlanogramListDialogs()}
          </div>
        </TabPanel>

        <TabPanel
          header={t("planogramsPage.tabs.pendings")}
          headerTemplate={renderPlanogramTabHeader(
            1,
            t("planogramsPage.tabs.pendings"),
          )}
        >
          <div className="data-card">
            {renderPlanogramPendingsDataTable()}
            {renderPlanogramListDialogs()}
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default Planograms;
